import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface AddTextContentModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onTitleChange: (value: string) => void;
  content: string;
  onContentChange: (value: string) => void;
  onAdd: () => void;
  isAdding: boolean;
}

export function AddTextContentModal({
  isOpen,
  onOpenChange,
  title,
  onTitleChange,
  content,
  onContentChange,
  onAdd,
  isAdding,
}: AddTextContentModalProps) {
  const { t } = useTranslation();
  const isValid = title.trim().length > 0 && content.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t('project.addTextContent')}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('project.textContentTitle')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={title}
                onChange={e => onTitleChange(e.target.value.slice(0, 32))}
                placeholder={t('project.textContentTitlePlaceholder')}
                className="w-full p-3 pr-14 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white"
                autoFocus
                maxLength={32}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                {title.length}/32
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('project.textContentContent')}
            </label>
            <textarea
              value={content}
              onChange={e => onContentChange(e.target.value)}
              placeholder={t('project.textContentPlaceholder')}
              rows={8}
              className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white resize-y min-h-[200px]"
            />
          </div>
        </div>
        <DialogFooter className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              onTitleChange('');
              onContentChange('');
            }}
            disabled={isAdding}
            className="px-4 py-2"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onAdd}
            disabled={!isValid || isAdding}
            className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
          >
            {isAdding ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                {t('common.adding')}
              </span>
            ) : (
              t('project.addContent')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddTextContentModal;
